import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { type ReactNode } from "react";

import { type IntentVariant } from "../../../ui-theme";
import { mergeSxProps } from "../../lib/mergeSxProps";
import { type UseModalState } from "../../lib/useModalState";
import { BottomSheet } from "../BottomSheet";
import { Card } from "../Card";
import { CbhIcon, type IconType } from "../CbhIcon";
import { Text } from "../Text";

export interface BottomSheetWithIntentProps {
  modalState: UseModalState;
  intentContent?: ReactNode;
  intent?: IntentVariant;
  intentIcon?: IconType;
  children?: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
}

export function BottomSheetWithIntent(props: BottomSheetWithIntentProps) {
  const { modalState, intentContent, intent, children, footer, onClose, intentIcon } = props;

  return (
    <BottomSheet modalState={modalState} footer={footer} onClose={onClose}>
      <Stack
        sx={{
          backgroundColor: (theme) =>
            isDefined(intent) && isDefined(intentContent)
              ? theme.palette.intent?.[intent].background
              : undefined,
        }}
      >
        {isDefined(intent) && isDefined(intentContent) && (
          <Stack>
            <Stack
              sx={{
                width: "100%",
                paddingX: 5,
                paddingTop: 5,
                flexDirection: "row",
                minHeight: 32,
                gap: 2,
              }}
            >
              <CbhIcon
                type={intentIcon ?? "triangle-exclamation"}
                size="small"
                sx={{ color: (theme) => theme.palette.intent?.[intent].icon }}
              />
              <Text variant="body2" sx={{ color: (theme) => theme.palette.intent?.[intent].text }}>
                {intentContent}
              </Text>
            </Stack>
          </Stack>
        )}
        <DialogContent
          sx={mergeSxProps(
            { paddingX: 0, height: "100%", paddingBottom: 0 },
            isDefined(intent) && isDefined(intentContent) ? { paddingTop: 5 } : { paddingTop: 0 }
          )}
        >
          <Card
            sx={{
              paddingTop: 5,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: (theme) => theme.borderRadius?.large,
              borderTopRightRadius: (theme) => theme.borderRadius?.large,
            }}
          >
            {children}
          </Card>
        </DialogContent>
      </Stack>
    </BottomSheet>
  );
}
